/**
 * Discount approval rules, shared by the Opportunity Products tab.
 *
 * These mirror the server's authority checks in
 * `backend/src/routes/opportunities.js` (the `/approve` and `/revert-approval`
 * endpoints). The server is authoritative; this exists so the UI offers exactly
 * the controls the server will honour, and so the rules can be tested without
 * mounting the 4,000-line detail page. If one side changes, change both — the
 * paired test suites are the tripwire.
 *
 * Tiers:
 *   ≤10% discount — an Admin approves outright.
 *   >10% discount — an Admin gives a first-pass endorsement, then a Super Admin
 *                   gives final approval.
 */

/** The subset of a line item these rules read. */
export interface DiscountLine {
  quantity?: number | string | null
  unit_price?: number | string | null
  discount?: number | string | null
  discount_status?: string | null
  discount_approved_by?: string | null
  discount_approved_by_type?: string | null
  discount_endorsed_by?: string | null
  renewal_justification?: string | null
}

export interface Viewer {
  isAdmin: boolean
  isSuperAdmin: boolean
  isImpersonating?: boolean
}

/** States a decision has actually been made in. */
const DECIDED = ['approved', 'endorsed', 'rejected']

const num = (v: unknown) => Number(v) || 0

/** Discount as a percentage of the undiscounted line total. */
export function discountPctOf(line: DiscountLine): number {
  const basis = num(line.quantity) * num(line.unit_price)
  return basis > 0 ? (num(line.discount) / basis) * 100 : 0
}

/**
 * Did this line need a Super Admin's sign-off?
 *
 * Rejection zeroes the discount, so on a rejected line the percentage reads 0%
 * however large it really was. Two pieces of evidence survive: an endorsement
 * stamp (only ever set on a >10% line), and who made the call. An authority
 * gate fails closed, so a Super Admin's decision takes a Super Admin to undo.
 */
export function requiresSuperAdminTier(line: DiscountLine): boolean {
  if (discountPctOf(line) > 10) return true
  if (line.discount_endorsed_by) return true
  return (
    line.discount_status === 'rejected' &&
    line.discount_approved_by_type === 'Super Admin'
  )
}

/** Human label for the authority level that signed a line off. */
export function approvalTierLabel(line: DiscountLine): string {
  return requiresSuperAdminTier(line)
    ? 'Super Admin approval (over 10%)'
    : 'Admin approval (10% or under)'
}

/** True when this user's "approve" is a first-pass endorsement, not a final approval. */
export function isFirstPassEndorsement(line: DiscountLine, viewer: Viewer): boolean {
  return (
    line.discount_status === 'pending' &&
    discountPctOf(line) > 10 &&
    !viewer.isSuperAdmin
  )
}

/**
 * May this user approve or reject the line right now?
 *
 * The status half of this matters as much as the role half: without it the
 * Approve/Reject controls kept rendering on lines that were already decided, so
 * an approved discount was indistinguishable from one still awaiting approval,
 * and re-approving re-stamped the row and re-sent every notification.
 */
export function canActOnDiscount(line: DiscountLine, viewer: Viewer): boolean {
  if (viewer.isImpersonating) return false
  if (line.discount_status !== 'pending' && line.discount_status !== 'endorsed') return false
  return line.discount_status === 'endorsed' ? viewer.isSuperAdmin : viewer.isAdmin
}

/** True when someone actually decided this line (vs. the legacy 'approved' default). */
export function hasDecisionStamp(line: DiscountLine): boolean {
  if (line.discount_status === 'approved') return !!line.discount_approved_by
  return DECIDED.includes(line.discount_status || '')
}

/**
 * May this user reverse the decision, returning the line to pending?
 *
 * Same tier that signed it off. Withdrawing a first-pass endorsement is itself
 * an Admin-tier act, so any Admin may do it.
 */
export function canRevertDiscount(line: DiscountLine, viewer: Viewer): boolean {
  if (viewer.isImpersonating) return false
  if (!DECIDED.includes(line.discount_status || '')) return false
  // A line sitting on the migration-055 DEFAULT of 'approved' was never decided.
  if (!hasDecisionStamp(line)) return false
  if (line.discount_status === 'endorsed') return viewer.isAdmin
  return requiresSuperAdminTier(line) ? viewer.isSuperAdmin : viewer.isAdmin
}

/**
 * Does the approval row render at all? A discount, an undecided/rejected line,
 * or an approved line someone actually signed off — the last case catches a
 * Workspace renewal price, which carries no discount of its own. An ordinary
 * line sits on the legacy 'approved' default with no approver, and stays hidden.
 */
export function showsApprovalRow(line: DiscountLine): boolean {
  if (num(line.discount) > 0) return true
  if (['pending', 'endorsed', 'rejected'].includes(line.discount_status || '')) return true
  return line.discount_status === 'approved' && !!line.discount_approved_by
}
